import { useAppSelector } from '../../config/hooks';
import {
  getBasket,
  getDeliveryFee,
  getDiscountAfterPromoCode,
  getDiscountForAdditionalSets,
  getFinalSum,
  getFinalSumAfterDiscountAndDeliveryFee,
  getLoyaltyPointsDiscount,
  getPickupPointDiscountAllDays,
  getPromoCode,
  getSelectedAdditionalFeesPrice,
} from '../../redux/selectors/basketSelector';
import { getAddressDetails } from '../../redux/selectors/summaryReducer.selectors';
import {
  getDiscount,
  getPrice,
  getPricePerDay,
  getPricePerDayBeforeDiscount,
  getSelectedSideOrdersPrice,
} from '../../utils/orderFormPrices';
import { BasketItemT } from '../../@types/basketItem.type';
import { useMemo } from 'react';
import { ProgramTypeE } from '../../@types/orderForm.type';

const useGenerateCalculation = () => {
  const basket: Array<BasketItemT> = useAppSelector(getBasket);
  const { city } = useAppSelector(getAddressDetails);
  const { code: promoCode } = useAppSelector(getPromoCode);
  const totalCostWithoutDiscounts = useAppSelector(getFinalSum);
  const totalDeliveryCost = useAppSelector(getDeliveryFee);
  const totalPromoCodeDiscount = useAppSelector(getDiscountAfterPromoCode);
  const totalDeliveriesOnDateDiscount = useAppSelector(
    getDiscountForAdditionalSets
  );
  const totalLoyaltyPointsDiscount = useAppSelector(getLoyaltyPointsDiscount);
  const totalPickupPointDiscount = useAppSelector(
    getPickupPointDiscountAllDays
  );
  const totalCostWithDiscounts = useAppSelector(
    getFinalSumAfterDiscountAndDeliveryFee
  );
  const totalOneTimeSideOrdersCost = useAppSelector(
    getSelectedAdditionalFeesPrice
  );
  const totalCostToPay = useAppSelector(getFinalSumAfterDiscountAndDeliveryFee);

  const totalOrderLengthDiscount = basket
    .map(curr => {
      const pricePerDay = getPricePerDayBeforeDiscount(curr, city.sectorId);

      return getDiscount(pricePerDay, curr.days, curr.sets, curr.selectedDiet, {
        code: '',
      });
    })
    .reduce((acc: any, discount: any) => acc + discount, 0);

  const parseNumber = (price?: number | null) =>
    typeof price === 'number' ? Number(price.toFixed(2)) : 0;

  return useMemo(
    () => ({
      cart: {
        totalCostToPay: parseNumber(totalCostToPay),
        totalCostWithoutDiscounts: parseNumber(totalCostWithoutDiscounts),
        totalCostWithDiscounts: parseNumber(totalCostWithDiscounts),
        totalDeliveryCost: parseNumber(totalDeliveryCost),
        totalPromoCodeDiscount: parseNumber(totalPromoCodeDiscount),
        totalOrderLengthDiscount: parseNumber(totalOrderLengthDiscount),
        totalDeliveriesOnDateDiscount: parseNumber(
          totalDeliveriesOnDateDiscount
        ),
        totalLoyaltyPointsDiscount: parseNumber(totalLoyaltyPointsDiscount),
        totalPickupPointDiscount: parseNumber(totalPickupPointDiscount),
        totalOneTimeSideOrdersCost: parseNumber(totalOneTimeSideOrdersCost),
      },
      items: basket.map((item: BasketItemT) => {
        const perDayDietCost =
          getPricePerDay(
            item,
            promoCode,
            city?.sectorId,
            item.selectedProgram === 'menuConfiguration'
          ) || 0;

        const totalDietWithoutSideOrdersCost =
          getPrice(item, promoCode, city?.sectorId) || 0;

        const totalSideOrdersCost =
          getSelectedSideOrdersPrice(item?.mealExchange?.days, item?.sets) || 0;

        const totalDietWithSideOrdersCost =
          totalDietWithoutSideOrdersCost + totalSideOrdersCost;

        const totalMealsChosenCost =
          item.selectedProgram === ProgramTypeE.MenuConfiguration
            ? item?.mealExchange?.totalPrice
                ?.changesOnMenuConfigurationBeforeDiscount * item.sets
            : 0;

        return {
          itemId: item.orderId,
          perDayDietCost: parseNumber(perDayDietCost),
          totalDietWithSideOrdersCost: parseNumber(totalDietWithSideOrdersCost),
          totalDietWithoutSideOrdersCost: parseNumber(
            totalDietWithoutSideOrdersCost
          ),
          totalSideOrdersCost: parseNumber(totalSideOrdersCost),
          totalMealsChosenCost: parseNumber(totalMealsChosenCost),
        };
      }),
    }),
    [
      basket,
      city?.sectorId,
      promoCode,
      totalCostToPay,
      totalCostWithDiscounts,
      totalCostWithoutDiscounts,
      totalDeliveriesOnDateDiscount,
      totalDeliveryCost,
      totalLoyaltyPointsDiscount,
      totalOneTimeSideOrdersCost,
      totalOrderLengthDiscount,
      totalPickupPointDiscount,
      totalPromoCodeDiscount,
    ]
  );
};

export default useGenerateCalculation;
